import { createClient } from '@supabase/supabase-js';
import { getApiToken } from '../services/api';

const supabaseUrl = 'https://culxgyrufratlvhnyhck.supabase.co';
const supabaseAnonKey =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImN1bHhneXJ1ZnJhdGx2aG55aGNrIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzM0MjY5MTEsImV4cCI6MjA4OTAwMjkxMX0.2hbZffPKfQHlDHSVXWKp9jgZV0LnhNrEN2G8hcC-1Go';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ─── User Profile ─────────────────────────────────────────────────────────────

export async function getUserProfile(userId) {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) throw error;
  return data;
}

// Alias for backward compatibility - AuthContext uses this
export async function getCurrentUser() {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError || !user) return { user: null, profile: null };

  const { data: profile, error: profileError } = await supabase
    .from('users')
    .select('*, organizations(name)')
    .eq('id', user.id)
    .single();

  return { user, profile, error: profileError };
}

// Build a normalized currentUser object from Supabase auth user + public.users row
// Uses backend API to bypass RLS issues
// Cache to avoid hammering /api/auth/me on every auth state change
const userCache = { token: null, user: null };

export function clearUserCache() {
  userCache.token = null;
  userCache.user = null;
}

export async function buildCurrentUser(authUser, tokenOverride) {
  try {
    let accessToken = tokenOverride || null;

    // If no token passed in, try to get a fresh one via getSession() (handles auto-refresh)
    if (!accessToken) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        accessToken = session?.access_token || null;
      } catch (_) {}
    }

    // Final fallback: read from localStorage
    if (!accessToken) {
      try {
        const key = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
        if (key) {
          const stored = JSON.parse(localStorage.getItem(key));
          accessToken = stored?.access_token || null;
        }
      } catch (_) {}
    }

    if (!accessToken) throw new Error('No session token available');

    // Return cached result if token hasn't changed
    if (userCache.token === accessToken && userCache.user) {
      return userCache.user;
    }

    // Call backend API which uses service role (bypasses RLS)
    const API_URL = process.env.REACT_APP_API_URL || '';
    const response = await fetch(`${API_URL}/api/auth/me`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      if (response.status === 403 && body.error === 'PENDING_APPROVAL') {
        throw Object.assign(new Error('PENDING_APPROVAL'), { code: 'PENDING_APPROVAL' });
      }
      if (response.status === 403 && body.error === 'ACCOUNT_REJECTED') {
        throw Object.assign(new Error('ACCOUNT_REJECTED'), { code: 'ACCOUNT_REJECTED' });
      }
      throw new Error(`API error: ${response.status}`);
    }

    const { user } = await response.json();

    // Fetch effective permissions for this user
    let permissions = null;
    try {
      const permRes = await fetch(`${API_URL}/api/auth/my-permissions`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (permRes.ok) {
        const permData = await permRes.json();
        permissions = permData.permissions;
      }
    } catch (_) {}

    const result = {
      id: user.id,
      email: user.email,
      name: user.name || '',
      orgId: user.orgId || null,
      orgName: user.orgName || null,
      role: user.role || 'member',
      avatarUrl: null,
      permissions,
    };

    userCache.token = accessToken;
    userCache.user = result;

    return result;
  } catch (err) {
    // Re-throw approval errors — App.js needs to handle these specifically
    if (err.code === 'PENDING_APPROVAL' || err.code === 'ACCOUNT_REJECTED') throw err;
    console.error('[buildCurrentUser] Error:', err);
    // Fallback to basic info from auth user
    return {
      id: authUser.id,
      email: authUser.email,
      name: authUser.user_metadata?.full_name || '',
      orgId: null,
      orgName: null,
      role: 'member',
      avatarUrl: null,
    };
  }
}

// ─── Organization ─────────────────────────────────────────────────────────────

export const ORG_TYPES = [
  { value: 'pe', label: 'Private Equity', description: 'Investing in and acquiring private companies' },
  { value: 'vc', label: 'Venture Capital', description: 'Funding early-stage and growth companies' },
  { value: 'corp_dev', label: 'Corporate Development', description: 'M&A and strategic investments for a corporation' },
  { value: 'family_office', label: 'Family Office', description: 'Managing investments for a high-net-worth family' },
  { value: 'real_estate', label: 'Real Estate', description: 'Real estate acquisitions and investments' },
  { value: 'consultant', label: 'M&A Consultant / Advisor', description: 'Advising multiple client firms — manage a separate workspace per client' },
];

export async function getOrgById(orgId) {
  if (!orgId) return null;
  try {
    const API_URL = process.env.REACT_APP_API_URL || '';
    const token = getApiToken();
    const res = await fetch(`${API_URL}/api/organizations/me`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (_) {
    return null;
  }
}

export async function getOrgMembers(orgId) {
  try {
    const API_URL = process.env.REACT_APP_API_URL || '';
    const token = getApiToken();
    const res = await fetch(`${API_URL}/api/organizations/me/users`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) return [];
    return await res.json();
  } catch (_) {
    return [];
  }
}

// ─── Invites ──────────────────────────────────────────────────────────────────

export async function getInviteByToken(token) {
  const API_URL = process.env.REACT_APP_API_URL || '';
  const res = await fetch(`${API_URL}/api/org/invite/${token}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || 'Invite not found');
  }
  return res.json();
}

// ─── Permissions ──────────────────────────────────────────────────────────────

export const RESOURCES = ['pipeline', 'contacts', 'documents', 'deals', 'settings', 'summaries', 'library', 'chats'];
export const ACTIONS = ['view', 'edit', 'delete', 'admin'];

export const DEFAULT_PERMISSIONS = {
  admin: { view: true, edit: true, delete: true, admin: true },
  member: { view: true, edit: true, delete: false, admin: false },
  viewer: { view: true, edit: false, delete: false, admin: false },
};

export async function getUserPermissions(userId) {
  try {
    const API_URL = process.env.REACT_APP_API_URL || '';
    let token = null;
    try {
      const key = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
      if (key) token = JSON.parse(localStorage.getItem(key))?.access_token;
    } catch (_) {}
    const res = await fetch(`${API_URL}/api/org/permissions/${userId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) return [];
    return await res.json();
  } catch (_) {
    return [];
  }
}
